/**
 * Onboard Flow Constants
 *
 * Configuration constants for the onboarding lead-magnet flow.
 *
 * @see {@link ./types.ts} for type definitions
 * @see {@link ./__tests__/schemas.test.ts} for schema tests that validate against these constants
 */

import type { OnboardStep, OnboardStepConfig } from './types'

/**
 * Valid onboard step identifiers for runtime validation.
 * Used to prevent open redirect via tampered session cookie.
 */
export const VALID_ONBOARD_STEPS: ReadonlySet<string> = new Set<OnboardStep>([
  'entry',
  'business-info',
  'intro-video',
  'assessment',
  'report',
  'pricing',
  'welcome',
])

/**
 * Onboard plan ID assigned to users created through the onboard flow
 */
export const ONBOARD_PLAN_ID = 20

/**
 * Onboard step configuration array
 */
export const ONBOARD_STEPS: OnboardStepConfig[] = [
  {
    id: 'entry',
    step: 1,
    title: 'Account',
    path: '/onboard',
    headline: 'See How Your Core Values Attract Your Ideal Buyers',
  },
  {
    id: 'business-info',
    step: 2,
    title: 'Business Info',
    path: '/onboard/business-info',
    headline: 'Tell us about you and your primary offer',
  },
  {
    id: 'intro-video',
    step: 3,
    title: 'Pre-Assessment',
    path: '/onboard/intro-video',
    headline: 'Watch this super-short video to get optimal results',
  },
  {
    id: 'assessment',
    step: 4,
    title: 'Assessment',
    path: '/onboard/assessment',
    headline: 'Free Pre-Assessment?',
  },
  {
    id: 'report',
    step: 5,
    title: 'Report',
    path: '/onboard/report',
    headline: 'Here are your three primary values',
  },
  {
    id: 'pricing',
    step: 6,
    title: 'Plans',
    path: 'https://bnsn.ai/plans/',
    headline: 'Choose your BNSN plan',
  },
  {
    id: 'welcome',
    step: 7,
    title: 'Welcome',
    path: '/onboard/welcome',
    headline: 'Welcome to BNSN',
  },
]

/**
 * Total number of visible onboard steps
 */
export const TOTAL_ONBOARD_STEPS = ONBOARD_STEPS.length

/**
 * Available offer type options for Step 2
 */
export const OFFER_TYPES = ['Book', 'Software', 'Course', 'Coaching', 'Service', 'Product', 'Other'] as const

export type OfferType = (typeof OFFER_TYPES)[number]

/**
 * Video URLs (placeholder until Jon provides final versions)
 */
export const VIDEO_URLS = {
  introVideo: 'https://app.membership.io/file/gqxWx4v4NP/embed',
  gettingStarted: 'https://app.membership.io/file/l2RZBOVx2a/embed',
} as const

/**
 * Copy text for various steps
 */
export const ONBOARD_COPY = {
  // Step 1: Account Creation
  entrySubheading: 'Get your free Buyer Alignment Profile™ and attract more ideal buyers',
  entryBody:
    "Welcome! We're really glad you're here.\n\nWhat you're about to do is pretty simple. It only takes about five minutes...but it might be one of the most useful things you do for your business this year.\n\nI'm Jon Benson. That's me with my wife, Olya. I've been a copywriter for 23 years, and I've helped create over a billion dollars in revenue for my clients. But the thing I'm most proud of though isn't that number. It's what I figured out behind it: when you weave your genuine core values into your copy, the right buyers find you. And they stick around.\n\nMost copywriters never do this. AI can't do it without the right training. That's why I built it into my copywriting AI, BNSN.\n\nMy wife isn't just my life partner, she's my business partner. She's our co-instructor in our Psychology of Success program, and she helped shape a lot of what makes this work. Just letting you know: we're a small family business, and we care a lot about getting this right for you.\n\nSo let's get started! We'll hone your top three core values and show you exactly how to put them to work in your copy...the words to say and not to say to help attract your ideal buyers.\n\nIf you have a BNSN account already, simply login. Or if you don’t, we’ll create a free one for you below.\n\nWe think you're going to love this!\n\nJon & Olya",
  entryButtonText: 'Create Account and Start Assessment',

  // Step 2: Business Information
  businessInfoBody:
    'To assess your values and weave them into your sales messaging, we need to know a bit about you and your primary offer. If you don’t have an offer yet, that’s cool - just tell us what you’d like to create and who it’s for.',
  businessInfoButtonText: 'Continue to Assessment',

  // Step 3: Introduction Video
  introVideoBody:
    'Most businesses spend years trying to find their ideal buyer. This does it in five minutes. Watch the video below to see how the Buyer Alignment assessment works and to ensure your report is laser-targeted to the buyers you want.',
  introVideoButtonText: "I'm ready. Let's begin.",

  // Step 4: Assessment
  assessmentBody:
    "Answer these ten questions honestly. There are no right or wrong answers. We're looking for what actually drives your decisions, not what you think should drive them.\n\nJust click the right arrow button below when you’re finished typing your answers to go to the next question.",
  assessmentButtonText: 'See my primary values',

  // Step 5: Report
  reportCta: 'Apply these values to your copy',

  // Step 6: Welcome
  welcomeBody:
    "Your values are being loaded. Watch this one minute video while we set up your account. Then we'll continue your first project.",
  welcomeButtonText: 'Continue your first project',
} as const
